import React, { useState, useRef } from "react";

const calculatePearson = (arrX, arrY) => {
  if (arrX.length !== arrY.length || arrX.length === 0) return 0;

  const n = arrX.length;
  const meanX = arrX.reduce((sum, val) => sum + val, 0) / n;
  const meanY = arrY.reduce((sum, val) => sum + val, 0) / n;

  let numerator = 0,
    denominatorX = 0,
    denominatorY = 0;

  for (let i = 0; i < n; i++) {
    const diffX = arrX[i] - meanX;
    const diffY = arrY[i] - meanY;
    numerator += diffX * diffY;
    denominatorX += diffX * diffX;
    denominatorY += diffY * diffY;
  }

  const denominator = Math.sqrt(denominatorX * denominatorY);
  return denominator === 0 ? 0 : numerator / denominator;
};

const App = () => {
  const [inputs, setInputs] = useState({
    name: "",
    id: "",
    time: "T0",
    map: Array(30).fill(""),
    icp: Array(30).fill(""),
    rso: Array(30).fill(""),
    dHHb: Array(30).fill(""),
    dO2Hb: Array(30).fill(""),
    dcHb: Array(30).fill(""),
  });

  const [output, setOutput] = useState({ prx: 0, tox: 0, hhbx: 0, o2hbx: 0, chbx: 0 });

  const inputRefs = useRef([]);

  const handleChange = (e, type, index = null) => {
    const { value } = e.target;
    if (type === "name" || type === "id" || type === "time") {
      setInputs({ ...inputs, [type]: value });
    } else {
      const newValues = [...inputs[type]];
      newValues[index] = value;
      setInputs({ ...inputs, [type]: newValues });
    }
  };

  const handleKeyDown = (e, index, type) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const nextIndex = inputRefs.current.findIndex((el, i) => i > index && el);
      if (nextIndex !== -1) {
        inputRefs.current[nextIndex].focus();
      }
    }
  };

  const calculate = async () => {
    const mapValues = inputs.map.map((val) => parseFloat(val) || 0);
    const icpValues = inputs.icp.map((val) => parseFloat(val) || 0);
    const rsoValues = inputs.rso.map((val) => parseFloat(val) || 0);
    const dHHbValues = inputs.dHHb.map((val) => parseFloat(val) || 0);
    const dO2HbValues = inputs.dO2Hb.map((val) => parseFloat(val) || 0);
    const dcHbValues = inputs.dcHb.map((val) => parseFloat(val) || 0);

    const prx = calculatePearson(mapValues, icpValues);
    const tox = calculatePearson(mapValues, rsoValues);
    const hhbx = calculatePearson(mapValues, dHHbValues);
    const o2hbx = calculatePearson(mapValues, dO2HbValues);
    const chbx = calculatePearson(mapValues, dcHbValues);
    console.log("New Output:", { prx, tox, hhbx, o2hbx, chbx }); // Debugging
    fetch('https://burnity-api.phamduylai.com/api/v1/admin/configs/addGoogleSheet', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: inputs.name,
        id: inputs.id,
        time: inputs.time,
        data: { "map": inputs.map, "icp": inputs.icp, "rso": inputs.rso, "dHHb": inputs.dHHb, "dO2Hb": inputs.dO2Hb, "dcHb": inputs.dcHb},
        PRx: prx.toFixed(4).replace('.', ','),
        TOx: tox.toFixed(4).replace('.', ','),
        HHbx: hhbx.toFixed(4).replace('.', ','),
        O2Hbx: o2hbx.toFixed(4).replace('.', ','),
        cHbx: chbx.toFixed(4).replace('.', ','),
      })
    });
    setOutput({ prx, tox, hhbx, o2hbx, chbx });
  };

  return (
    <div style={{
      padding: "20px 10px",
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      <div style={{
        maxWidth: "1200px",
        margin: "auto",
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        padding: "15px 12px"
      }}>
        <h2 style={{
          textAlign: "center",
          color: "#333",
          fontSize: "clamp(18px, 5vw, 24px)",
          marginBottom: "15px",
          fontWeight: "600"
        }}>Medical Data Calculator</h2>

        {/* Patient Info Section */}
        <div style={{
          background: "#f8f9fa",
          padding: "12px",
          borderRadius: "8px",
          marginBottom: "15px",
          border: "1px solid #e0e0e0"
        }}>
          <h3 style={{
            color: "#555",
            fontSize: "14px",
            marginBottom: "10px",
            fontWeight: "600"
          }}>Patient Information</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px" }}>
            <div>
              <label style={{
                display: "block",
                marginBottom: "4px",
                color: "#555",
                fontWeight: "500",
                fontSize: "11px"
              }}>Name</label>
              <input
                type="text"
                value={inputs.name}
                onChange={(e) => handleChange(e, "name")}
                ref={(el) => (inputRefs.current[0] = el)}
                onKeyDown={(e) => handleKeyDown(e, 0)}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "12px",
                  transition: "border-color 0.2s",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#667eea"}
                onBlur={(e) => e.target.style.borderColor = "#ddd"}
              />
            </div>

            <div>
              <label style={{
                display: "block",
                marginBottom: "4px",
                color: "#555",
                fontWeight: "500",
                fontSize: "11px"
              }}>ID</label>
              <input
                type="text"
                value={inputs.id}
                onChange={(e) => handleChange(e, "id")}
                ref={(el) => (inputRefs.current[1] = el)}
                onKeyDown={(e) => handleKeyDown(e, 1)}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "12px",
                  transition: "border-color 0.2s",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#667eea"}
                onBlur={(e) => e.target.style.borderColor = "#ddd"}
              />
            </div>

            <div>
              <label style={{
                display: "block",
                marginBottom: "4px",
                color: "#555",
                fontWeight: "500",
                fontSize: "11px"
              }}>Thời điểm</label>
              <select
                value={inputs.time}
                onChange={(e) => handleChange(e, "time")}
                ref={(el) => (inputRefs.current[2] = el)}
                onKeyDown={(e) => handleKeyDown(e, 2)}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "12px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  outline: "none"
                }}
              >
                <option value="T0">T0</option>
                <option value="T30">T30</option>
                <option value="T60">T60</option>
                <option value="T120">T120</option>
                <option value="T240">T240</option>
                <option value="T360">T360</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Entry Section */}
        <div style={{
          background: "#f8f9fa",
          padding: "12px",
          borderRadius: "8px",
          marginBottom: "15px",
          border: "1px solid #e0e0e0"
        }}>
          <h3 style={{
            color: "#555",
            fontSize: "14px",
            marginBottom: "10px",
            fontWeight: "600"
          }}>Measurement Data (30 data points)</h3>

          <div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: "3px",
              fontWeight: "bold",
              marginBottom: "6px",
              padding: "5px 3px",
              background: "#667eea",
              borderRadius: "4px",
              color: "white",
              fontSize: "9px",
              textAlign: "center"
            }}>
              <span>MAP</span>
              <span>ICP</span>
              <span>rSO</span>
              <span>d.HHb</span>
              <span>d.O2Hb</span>
              <span>d.cHb</span>
            </div>

            <div>
            {inputs.map.map((_, index) => (
              <div key={index} style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: "3px",
                marginBottom: "4px",
                padding: "1px",
                background: index % 2 === 0 ? "white" : "#fafafa",
                borderRadius: "3px"
              }}>
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.map[index]}
                  onChange={(e) => handleChange(e, "map", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 3] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 3)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.icp[index]}
                  onChange={(e) => handleChange(e, "icp", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 4] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 4)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.rso[index]}
                  onChange={(e) => handleChange(e, "rso", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 5] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 5)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.dHHb[index]}
                  onChange={(e) => handleChange(e, "dHHb", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 6] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 6)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.dO2Hb[index]}
                  onChange={(e) => handleChange(e, "dO2Hb", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 7] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 7)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
                <input
                  type="number"
                  placeholder={`${index + 1}`}
                  value={inputs.dcHb[index]}
                  onChange={(e) => handleChange(e, "dcHb", index)}
                  ref={(el) => (inputRefs.current[index * 6 + 8] = el)}
                  onKeyDown={(e) => handleKeyDown(e, index * 6 + 8)}
                  style={{
                    padding: "3px",
                    border: "1px solid #ddd",
                    borderRadius: "3px",
                    fontSize: "10px",
                    textAlign: "center",
                    outline: "none",
                    transition: "all 0.2s",
                    minWidth: 0
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#667eea";
                    e.target.style.background = "#f0f4ff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#ddd";
                    e.target.style.background = "white";
                  }}
                />
              </div>
            ))}
            </div>
          </div>
        </div>

        <button
          onClick={calculate}
          disabled={inputs.map.includes("") || inputs.icp.includes("") || inputs.rso.includes("") || inputs.dHHb.includes("") || inputs.dO2Hb.includes("") || inputs.dcHb.includes("")}
          style={{
            width: "100%",
            padding: "10px",
            fontSize: "14px",
            fontWeight: "600",
            color: "white",
            background: inputs.map.includes("") || inputs.icp.includes("") || inputs.rso.includes("") || inputs.dHHb.includes("") || inputs.dO2Hb.includes("") || inputs.dcHb.includes("")
              ? "#ccc"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
            borderRadius: "8px",
            cursor: inputs.map.includes("") || inputs.icp.includes("") || inputs.rso.includes("") || inputs.dHHb.includes("") || inputs.dO2Hb.includes("") || inputs.dcHb.includes("")
              ? "not-allowed"
              : "pointer",
            transition: "all 0.2s",
            boxShadow: inputs.map.includes("") || inputs.icp.includes("") || inputs.rso.includes("") || inputs.dHHb.includes("") || inputs.dO2Hb.includes("") || inputs.dcHb.includes("")
              ? "none"
              : "0 3px 10px rgba(102, 126, 234, 0.3)",
            marginBottom: "15px"
          }}
          onMouseEnter={(e) => {
            if (!e.target.disabled) {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.5)";
            }
          }}
          onMouseLeave={(e) => {
            if (!e.target.disabled) {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 3px 10px rgba(102, 126, 234, 0.3)";
            }
          }}
        >
          Calculate Correlations
        </button>

        {/* Results Section */}
        <div style={{
          background: "#f8f9fa",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid #e0e0e0"
        }}>
          <h3 style={{
            color: "#555",
            fontSize: "14px",
            marginBottom: "10px",
            fontWeight: "600",
            textAlign: "center"
          }}>Pearson Correlation Results</h3>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
            gap: "8px"
          }}>
            {[
              { label: "PRx", desc: "MAP vs ICP", value: output.prx, color: "#667eea" },
              { label: "TOx", desc: "MAP vs rSO", value: output.tox, color: "#764ba2" },
              { label: "HHbx", desc: "MAP vs d.HHb", value: output.hhbx, color: "#f093fb" },
              { label: "O2Hbx", desc: "MAP vs d.O2Hb", value: output.o2hbx, color: "#4facfe" },
              { label: "cHbx", desc: "MAP vs d.cHb", value: output.chbx, color: "#43e97b" }
            ].map((item, idx) => (
              <div key={idx} style={{
                background: "white",
                padding: "10px",
                borderRadius: "6px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                textAlign: "center",
                border: `2px solid ${item.color}`
              }}>
                <div style={{
                  fontSize: "9px",
                  color: "#888",
                  marginBottom: "4px",
                  fontWeight: "500"
                }}>{item.desc}</div>
                <div style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: item.color,
                  marginBottom: "2px"
                }}>{item.value.toFixed(4)}</div>
                <div style={{
                  fontSize: "12px",
                  color: "#555",
                  fontWeight: "600"
                }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;